import React, { useState } from "react";
import { gql } from "apollo-boost";
import { useMutation } from "@apollo/react-hooks";
import { modalBGColor } from "utils/customStyles";
import SuccessMessage from "components/Popups/Messages/SuccessMessage";
import { Button, Checkbox } from "components/common";
import { ERROR_STATUS } from "utils/constants";

export const HONOR_DISPUTED_TRANSACTION = gql`
  mutation honorDisputedTransaction(
    $transaction_ids: [Int]
    $dispute_ids: [Int]
  ) {
    honorDisputedTransaction(
      transaction_ids: $transaction_ids
      dispute_ids: $dispute_ids
    ) {
      status
      code
      message
    }
  }
`;

const DisputeCheckButton = ({
  removeId,
  addId,
  loading,
  label,
  setErrorMessage
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleClick = () => { 
    if (!isChecked) { 
      setErrorMessage("");
      setIsChecked(!isChecked);
      addId();
      return;
    } else { 
      removeId();
    }   
    setIsChecked(!isChecked); 
};

  return (
    <Checkbox
      label={label}
      checked={isChecked}
      disabled={loading}
      onChange={handleClick}
    />
  );
};

const HonorDisputesModal = ({ refetchDisputes, data }) => {
  const [disputeIds, setDisputeIds] = useState([]);
  const [transactionIds, setTransactionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [honorDisputedTransaction] = useMutation(HONOR_DISPUTED_TRANSACTION);

  const NOT_AVAILABLE = "not available";

  const addId = (setter, state, item_id) => {
    setter([...state, item_id]);
  };

  const removeId = (setter, state, item_id) => {
    setter(state.filter(id => id !== item_id));
  };

  let disputes_table_rows;
  if (data?.showDisputedTransactions?.user_disputes?.[0]) {
    disputes_table_rows = data.showDisputedTransactions.user_disputes.map(
      dispute => {
        return (
          <tr key={dispute.id}>
            <td>
              {dispute.disputed_amount
                ? dispute.disputed_amount
                : NOT_AVAILABLE}
            </td>
            <td>
              {dispute.granted_amount ? dispute.granted_amount : NOT_AVAILABLE}
            </td>
            <td>{dispute.outcome ? dispute.outcome : NOT_AVAILABLE}</td>
            <td>{dispute.status ? dispute.status : NOT_AVAILABLE}</td>
            <td>
              <DisputeCheckButton
                removeId={() => {
                  removeId(setDisputeIds, disputeIds, dispute.id);
                }}
                addId={() => {
                  addId(setDisputeIds, disputeIds, dispute.id);
                }}
                loading={loading}
                label="Settle"
                setErrorMessage={setErrorMessage}
              />
            </td>
          </tr>
        );
      }
    );
  } else {
    disputes_table_rows = (
      <tr>
        <td>No Dispute Found</td>
        <td>No Dispute Found</td>
        <td>No Dispute Found</td>
        <td>No Dispute Found</td>
      </tr>
    );
  }

  let hidden_transactions_table_rows;
  if (data?.showDisputedTransactions?.dispute_transactions?.[0]) {
    hidden_transactions_table_rows = data.showDisputedTransactions.dispute_transactions.map(
      transaction => {
        return (
          <tr key={transaction.id}>
            <td>{transaction.amount ? transaction.amount : NOT_AVAILABLE}</td>
            <td>
              {transaction.description
                ? transaction.description
                : NOT_AVAILABLE}
            </td>
            <td>{transaction.status ? transaction.status : NOT_AVAILABLE}</td>
            <td className="w-1/5">
              <DisputeCheckButton
                removeId={() => {
                  removeId(setTransactionIds, transactionIds, transaction.id);
                }}
                addId={() => {
                  addId(setTransactionIds, transactionIds, transaction.id);
                }}
                loading={loading}
                label="Unhide"
                setErrorMessage={setErrorMessage}
              />
            </td>
          </tr>
        );
      }
    );
  } else {
    hidden_transactions_table_rows = (
      <tr>
        <td>No Transactions Found</td>
        <td>No Transactions Found</td>
        <td>No Transactions Found</td>
      </tr>
    );
  }

  const handleSubmit = async () => {
    if (!transactionIds?.length && !disputeIds?.length) {
      setErrorMessage(
        "Please select at least one hidden transaction, and/or one dispute."
      );
      return;
    }

    setLoading(true);
    const transaction_ids = transactionIds.map(id => parseInt(id, 10));
    const dispute_ids = disputeIds.map(id => parseInt(id, 10));
    const res = await honorDisputedTransaction({
      variables: {
        transaction_ids,
        dispute_ids
      }
    });

    const {
      data: {
        honorDisputedTransaction: { status, message }
      }
    } = res;

    if (status === ERROR_STATUS) {
      setErrorMessage(message);
      setLoading(false);
      return;
    }

    setLoading(false);
    setSuccess(true);
    refetchDisputes();
  };

  return (
    <div
      style={{ ...modalBGColor, maxHeight: "100vh", overflowY: "scroll" }}
      className="py-8 px-12"
    >
      {!success ? (
        <div
          style={{ width: "600px" }}
          className="flex flex-col justify-center items-center"
        >
          <p>
            <span className="text-2xl font-bold">Hidden Transactions: </span>
            (all disputes are hidden transactions, but not all hidden
            transactions are disputes)
          </p>
          <table className="tomo-table">
            <thead>
              <tr>
                <th>Amount</th>
                <th>Description</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>{data && hidden_transactions_table_rows}</tbody>
          </table>
          <p className="mt-6 w-full">
            <span className="text-2xl font-bold mt-4">Disputes: </span>
            (most newer disputes will not have this table)
          </p>
          <table className="tomo-table">
            <thead>
              <tr>
                <th>Disputed Amount</th>
                <th>Granted Amount</th>
                <th>Outcome</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>{data && disputes_table_rows}</tbody>
          </table>
          <div className={errorMessage ? "text-red-600 mb-4" : "hidden"}>
            {errorMessage}
          </div>
          <Button
            className={`mb-6 mt-5 w-36 text-2xl btn-green ${
              loading ? "opacity-50" : ""
            }`}
            disabled={loading}
            onClick={() => {
              handleSubmit();
            }}
          >
            Submit
          </Button>
        </div>
      ) : (
        <SuccessMessage mainMessage="You have successfully honored the dispute." />
      )}
    </div>
  );
};

export default HonorDisputesModal;
